import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"

import "./index.css"
import { routeTree } from "./routeTree.gen"
import { auth } from "./lib/auth"
import { initTheme } from "./lib/theme"
import { ErrorScreen } from "./components/ErrorScreen"

initTheme()

// Offline shell + web push live in /sw.js (served from web/public).
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined)
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => {
        // 401s/403s are handled inside the api wrapper — no need to
        // retry them at the React Query layer.
        const status = (err as { status?: number })?.status
        if (status === 401 || status === 403 || status === 402) return false
        return count < 2
      },
    },
  },
})

// Router context — pass auth + queryClient so loaders can use both.
// No basepath: the SPA is served at the root (`/`).
const router = createRouter({
  routeTree,
  context: { auth, queryClient },
  defaultPreload: "intent",
  defaultErrorComponent: ({ error, reset }) => <ErrorScreen error={error} reset={reset} />,
  defaultNotFoundComponent: () => (
    <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
      Not found.
    </div>
  ),
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
